import { Button } from "@/ingot";
import { lazy, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { ConfiguratorPageShell } from "@/components/ConfiguratorPageShell";
import { ShopNotOrderableNotice } from "@/components/ShopNotOrderableNotice";
import { DxfDimensionOverlay } from "@/components/dxf/DxfDimensionOverlay";
import { DxfMeasurementOverlay } from "@/components/dxf/DxfMeasurementOverlay";
import { DxfWarningOverlay } from "@/components/dxf/DxfWarningOverlay";
import { RepairCompare } from "@/components/dxf/RepairCompare";
import {
  DxfPreview,
  type DxfPreviewStats,
  type PreviewMode,
} from "@/components/DxfPreview";
import { InteractiveControls } from "@/processes/dynamic/InteractiveControls";
import { InteractiveViewer } from "@/processes/dynamic/InteractiveViewer";
import { rotationOfInputs } from "@/processes/dynamic/interactiveInputs";
import { useInteractiveReanalysis } from "@/processes/dynamic/useInteractiveReanalysis";
import { useCart, useCatalog, useQuote, useQuoteBatch } from "@/api/queries";
import { useIsCurrentOrgAdmin } from "@/api/queries/orgs";
import { useProcesses } from "@/api/queries/processes";
import { useQuoteRepair } from "@/api/queries/quotes";
import { partPreviewType } from "@/processes/partPreviewType";
import { createModule, findModule } from "@/processes/registry";
import { DynamicPrimaryConfigurator } from "@/processes/dynamic/DynamicPrimaryConfigurator";
import type { AnalysisSnapshot, Process } from "@/api/types";

// Lazy-loaded — three.js + R3F bundle (~200 kB gz) only ships when a
// mesh-preview quote is actually rendered. Keeps the SVG flow's bundle
// lean.
const MeshPreview = lazy(() => import("@/components/MeshPreview"));

// Minimal ``Process`` derived from the quote itself, used when the
// quote's process is missing from ``/api/v1/processes`` (e.g. the

import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

// Stand-in used ONLY for page chrome when ``/processes`` has not resolved
// (endpoint errored, process unpublished mid-session). It carries no
// ``configurator_schema`` on purpose — the configurator must NOT be driven from
// it, or a load failure would render as "this process has no inputs".
function fallbackProcess(name: string): Process {
  return {
    name,
    slug: name,
    capability_key: name,
    display_name: name,
    description: "",
    icon: "",
    color: "slate",
    material_category: "",
    landing_order: 0,
    is_available: true,
    accepted_file_types: [],
    requires_file: true,
  };
}

import { useParsedDxfPreview } from "@/lib/dxfParse";
import { useSeo } from "@/lib/seo";
import { cartPath, checkoutPath } from "@/lib/storefrontPaths";
import { cx } from "@/lib/utils";

function previewStats(analysis: AnalysisSnapshot): DxfPreviewStats | undefined {
  // DXF tuple shape only — the 3D analyzer returns a dict for
  // ``bounding_box`` and the preview stats panel is DXF-only anyway.
  const bbox = Array.isArray(analysis.bounding_box) ? analysis.bounding_box : null;
  if (!bbox) return undefined;
  return {
    widthMm: bbox[2] - bbox[0],
    heightMm: bbox[3] - bbox[1],
    holeCount: analysis.hole_count ?? 0,
    totalCutMm: analysis.total_length ?? 0,
  };
}

/**
 * Quote configurator page.
 *
 * Layout (wide):
 *   ┌─ breadcrumb ────────────────────────────────────────────┐
 *   ┌─ status pill · ID                        replace · share ┐
 *   ┌──────────────── preview card ─────┬─ stepped configurator ─┐
 *   │   (file name lives in preview     │                          │
 *   │    header now)                    │                          │
 *   │   warnings panel                  │                          │
 *   │   analysis stats strip            │                          │
 *   │   tok výroby                      │                          │
 *   └───────────────────────────────────┴──────────────────────────┘
 *
 * On a small screen the layout stacks (preview first) so the user
 * sees what they uploaded before fiddling.
 */
export default function QuoteDetail(): JSX.Element {
  const { t: tQuote } = useTranslation("quote");
  const { lang: langParam, quoteId } = useParams<{ lang: string; quoteId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin: isShopAdmin } = useIsCurrentOrgAdmin();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const quoteSlug = quoteId ?? null;

  useSeo({
    title: quoteId
      ? `${tQuote("seo.title")} · ${quoteId}`
      : tQuote("seo.title"),
    path: `/quote/${quoteId ?? ""}`,
    lang,
  });

  const batchId = searchParams.get("batch");
  const batchQuery = useQuoteBatch(batchId);
  const batchInfo = useMemo(() => {
    if (!batchId || !batchQuery.data) return null;
    const quotes = batchQuery.data.quotes;
    const idx = quotes.findIndex((q) => q.id === quoteSlug);
    if (idx < 0) return null;
    return {
      publicId: batchId,
      current: idx,
      total: quotes.length,
      prev: idx > 0 ? quotes[idx - 1].id : null,
      next: idx < quotes.length - 1 ? quotes[idx + 1].id : null,
    };
  }, [batchId, batchQuery.data, quoteSlug]);

  const editLineId = (() => {
    const raw = searchParams.get("edit");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const cartQuery = useCart({ enabled: editLineId !== null });
  const editLine = useMemo(() => {
    if (editLineId === null || !cartQuery.data) return undefined;
    for (const item of cartQuery.data.items) {
      for (const line of item.lines) {
        if (line.id === editLineId) return line;
      }
    }
    return undefined;
  }, [editLineId, cartQuery.data]);

  const quoteQuery = useQuote(quoteSlug);
  // KAN-1703 — interaktivní vstupy dílu (otočení 3D modelu, …): ovládání ve
  // vizualizaci, okamžitá odezva a přepočet přes generickou přeanalýzu.
  const interactive = useInteractiveReanalysis(quoteQuery.data);
  const catalogQuery = useCatalog(lang);
  const processesQuery = useProcesses();

  const [previewMode, setPreviewMode] = useState<PreviewMode>("pan");
  // Live ``fieldKey → chosen-option hex`` lifted from the configurator (a sibling
  // of the preview pane) so a bound ``model3d`` view-spec block tints to the
  // customer's "Barva" pick. Empty until the configurator reports.
  const [boundColors, setBoundColors] = useState<Record<string, string>>({});
  const parsedPreview = useParsedDxfPreview(quoteQuery.data?.preview_url);
  // Only ask for the repair payload when the BE flagged this quote as
  // having one — otherwise the endpoint just 404s (clean DXFs and
  // tenants who disabled auto-repair both land here) and the noise
  // shows up in dev tools on every cart visit.
  const repairQuery = useQuoteRepair(quoteSlug, {
    enabled: quoteQuery.data?.has_repair === true,
  });

  // Stavy TÉHLE stránky, ne stavy uploadu. Do KAN-770 tu stálo
  // „Analyzuji soubory…" a „Nahrání selhalo. Zkuste to prosím znovu." —
  // obojí lhalo: analýza proběhla už při zakládání nabídky (odpověď POSTu
  // sem přiteče v cache) a tahle stránka nic nenahrává. U operace bez
  // souboru (``requires_file: false``) to lhalo dvakrát, protože zákazník
  // dostal radu zopakovat upload, který nikdy neexistoval.
  if (quoteQuery.isLoading || catalogQuery.isLoading || processesQuery.isLoading) {
    return (
      <main className="mx-auto max-w-5xl px-6 py-20 text-center text-ink-3">
        {tQuote("page.loading")}
      </main>
    );
  }

  if (quoteQuery.error || !quoteQuery.data) {
    return (
      <main className="mx-auto max-w-md px-6 py-20 text-center">
        <p className="text-lg text-ink-2">{tQuote("page.loadError")}</p>
        <Link to={`/${lang}`} className="mt-4 inline-block text-ink-3 underline">
          ←
        </Link>
      </main>
    );
  }

  const quote = quoteQuery.data;
  const stats = previewStats(quote.analysis);

  // Customer's chosen "Barva" hex for the mesh-preview path (preview_type
  // "mesh" → uploaded STL via ``MeshPreview``, which — unlike the out_viz
  // ``InteractiveViewer`` path — has no model3d ``color_field`` to read).
  // ``boundColors`` is the resolved ``fieldKey → hex`` map lifted from the
  // configurator (SSoT: hex comes from ``material.option_sources``); a mesh
  // preview carries a single colour pick, so the first resolved hex tints it.
  // ``undefined`` (nothing picked) → ``MeshPreview`` keeps its default violet.
  const meshColor = Object.values(boundColors)[0];

  // Resolve module by quote.process_type so the shell + preview render
  // from the process metadata (``preview_type`` / ``configurator_schema``).
  // The fallback only covers a missing /processes row — the loading
  // guard above already waited for the list.
  const processName = String(quote.process_type ?? "");
  // Two different questions, deliberately kept apart.
  //
  // ``resolvedModule`` is "did we actually get this process from the API?" — it
  // is null when the list has not resolved (endpoint down, process unpublished
  // mid-session, entitlement change). The CONFIGURATOR keys off this, because a
  // synthesised stand-in carries no ``configurator_schema`` and the card would
  // then claim the process has no inputs. A load failure and an unconfigured
  // process must not look the same to the customer.
  //
  // ``module`` is the page CHROME (breadcrumb, preview type, production flow).
  // A stand-in is fine there — the page still shows the part and its analysis —
  // so the stand-in stays for that, and only for that.
  const resolvedModule = findModule(processesQuery.data, processName);
  const module = resolvedModule ?? createModule(fallbackProcess(processName));

  // G5 — the preview is keyed by THIS part's ``preview_type`` (KAN-1401:
  // one operation may take 2D and 3D files); the process's declared value
  // is the fallback. mesh → three.js viewer, svg / unknown → vector preview.
  const previewType = partPreviewType(quote.preview_type, module.preview_type);
  const usesMeshPreview = previewType === "mesh";

  const replaceLabel = tQuote("actions.replaceFile", {
    defaultValue: "Nahradit soubor",
  });
  const replaceModalTitle = replaceLabel;
  // KAN-1027 — „Tok výroby" musí ukázat CELOU cestu dílu, ne jen jeho
  // primárku. Do té doby tu stálo natvrdo ``[processName]``, takže
  // zákazník, který si přidal povrchovou úpravu, viděl pořád jen
  // Přijetí → 3D tisk → Odeslání a krok, který si právě zvolil (a
  // který tvořil polovinu ceny), ve schématu nebyl.
  //
  // Pořadí je ``order_index`` = pořadí v řetězu; primárka (index 0) je
  // v ``quote.operations`` taky, takže se dedupuje proti ``processName``
  // — dvakrát zvýrazněný uzel by v grafu vypadal jako dvě operace.
  const chainProcessTypes = Array.from(
    new Set([
      processName,
      ...[...(quote.operations ?? [])]
        .sort((a, b) => a.order_index - b.order_index)
        .map((op) => op.process_type)
        .filter(Boolean),
    ]),
  );

  const breadcrumb = [
    { label: tQuote("page.breadcrumbUpload"), href: `/${lang}` },
    { label: tQuote("page.breadcrumbConfig") },
    { label: tQuote("page.breadcrumbCart"), href: cartPath(lang) },
    // KAN-922 — same helper as the cart's "continue to checkout" button;
    // a literal here used to 404.
    { label: tQuote("page.breadcrumbCheckout"), href: checkoutPath(lang) },
  ];

  const batchNav = batchInfo ? (
    <nav
      className="flex items-center justify-between rounded-md border border-border bg-surface-2 px-4 py-2 text-sm"
      data-testid="batch-nav"
    >
      <Link
        to={`/${lang}/quote-batch/${batchInfo.publicId}`}
        className="text-ink-2 hover:text-ink"
        data-testid="batch-nav-back"
      >
        {tQuote("batch.navBack", {
          current: batchInfo.current + 1,
          total: batchInfo.total,
        })}
      </Link>
      <div className="flex items-center gap-4">
        {batchInfo.prev ? (
          <Link
            to={`/${lang}/quote/${batchInfo.prev}?batch=${batchInfo.publicId}`}
            className="text-ink-2 hover:text-ink"
            data-testid="batch-nav-prev"
          >
            {tQuote("batch.navPrev")}
          </Link>
        ) : (
          <span className={cx("text-ink-4")}>{tQuote("batch.navPrev")}</span>
        )}
        {batchInfo.next ? (
          <Link
            to={`/${lang}/quote/${batchInfo.next}?batch=${batchInfo.publicId}`}
            className="text-ink-2 hover:text-ink"
            data-testid="batch-nav-next"
          >
            {tQuote("batch.navNext")}
          </Link>
        ) : (
          <span className={cx("text-ink-4")}>{tQuote("batch.navNext")}</span>
        )}
      </div>
    </nav>
  ) : null;
  // KAN-2096 — obchod, který objednávku nedokončí, to řekne už tady na
  // začátku konfigurace, ne až v pokladně.
  const topBanner = (
    <>
      <ShopNotOrderableNotice />
      {batchNav}
    </>
  );

  const preview = quote.is_view_only ? (
    // View-only quote (out_viz): the module's interactive view-spec IS the
    // customer's preview — render it in the main preview pane (never the raw
    // source URL, which ``preview_url`` points at for these quotes).
    quote.view_spec_url ? (
      <InteractiveViewer
        url={quote.view_spec_url}
        sourceUrl={quote.mesh_preview_url ?? quote.preview_url}
        boundColors={boundColors}
        interactive={interactive.binding}
      />
    ) : (
      <div className="flex aspect-[4/3] items-center justify-center rounded-md border border-border bg-surface text-sm text-ink-4">
        preview
      </div>
    )
  ) : !quote.preview_url ? (
    // Tahle nabídka nemá co ukázat — typicky operace bez souboru
    // (``requires_file: false``). Do KAN-770 sem šel prázdný rámeček
    // s natvrdo napsaným anglickým „preview"; zákazník ho ale nikdy
    // neviděl, protože stránka do té doby padala na 500. Renderuj radši
    // nic, stejně jako větev ``preview_type === "none"`` níž.
    null
  ) : usesMeshPreview ? (
    <section
      className="rounded-md border border-border bg-surface"
      data-testid="mesh-viewer-card"
    >
      <Suspense
        fallback={
          <div className="flex aspect-[4/3] items-center justify-center text-sm text-ink-4">
            {tQuote("upload.analyzing")}
          </div>
        }
      >
        <MeshPreview
          src={quote.preview_url}
          rotation={rotationOfInputs(interactive.binding?.inputs) ?? { x: 0, y: 0, z: 0 }}
          fileName={quote.file_name}
          color={meshColor}
          height={520}
        />
      </Suspense>
      {/* Náhled nahraného 3D dílu JE jeho model3d blok — ovládání deklarované
          na ``model3d`` patří sem stejně jako do view-specu. */}
      <InteractiveControls binding={interactive.binding} viewKind="model3d" />
    </section>
  ) : previewType === "none" ? (
    // The process declares no part preview (e.g. an analyzer-only mesh op) and no
    // admin visualization is authored → render nothing, rather than forcing the
    // raw source through the DXF/vector viewer (which can't render it).
    null
  ) : (
    <DxfPreview
      src={quote.preview_url}
      alt={quote.file_name}
      fileName={quote.file_name}
      badge={tQuote("preview.badgeVector")}
      scaleLabel="1:1"
      stats={stats}
      mode={previewMode}
      onModeChange={setPreviewMode}
      overlay={
        previewMode === "measure" && stats ? (
          <DxfMeasurementOverlay
            parsed={parsedPreview}
            widthMm={stats.widthMm}
            heightMm={stats.heightMm}
          />
        ) : (
          <>
            {stats && (
              <DxfDimensionOverlay
                widthMm={stats.widthMm}
                heightMm={stats.heightMm}
              />
            )}
            <DxfWarningOverlay
              warnings={quote.analysis.warnings}
              parsed={parsedPreview}
            />
          </>
        )
      }
    />
  );

  // RepairCompare slots between the validation panel and the analysis
  // stats — only renders when the BE flagged the quote with a repair
  // payload (see ``useQuoteRepair`` enabled guard above).
  const belowAnalysis =
    repairQuery.data && quote.preview_url ? (
      <RepairCompare
        quoteSlug={quote.id}
        fileName={quote.file_name}
        originalPreviewUrl={quote.preview_url}
        repair={repairQuery.data}
      />
    ) : null;

  const navigateAfterAdd = () => {
    if (editLine) {
      navigate(`/${lang}/cart`);
    } else if (batchInfo) {
      navigate(`/${lang}/quote-batch/${batchInfo.publicId}`);
    } else {
      navigate(`/${lang}/cart`);
    }
  };

  return (
    <ConfiguratorPageShell
      module={module}
      quoteId={quote.id}
      fileName={usesMeshPreview ? quote.file_name : ""}
      selectedProcessTypes={chainProcessTypes}
      // KAN-2020 — „Tok výroby" (Přijetí objednávky → Laserové řezání →
      // Odeslání se štítky SYSTÉM / ŘEZÁNÍ) je vnitřní pohled dílny:
      // zákazník ho nečte jako službu, ale jako žargon. Vidí ho jen správce
      // obchodu při náhledu konfigurátoru.
      showProductionFlow={(module.show_production_flow ?? false) && isShopAdmin}
      breadcrumb={breadcrumb}
      replaceLabel={replaceLabel}
      replaceModalTitle={replaceModalTitle}
      analysis={quote.analysis}
      lang={lang}
      preview={preview}
      topBanner={topBanner}
      belowAnalysis={belowAnalysis}
    >
      {!catalogQuery.data ? (
        <p className="text-sm text-ink-3">{tQuote("price.loading")}</p>
      ) : !resolvedModule ? (
        // The process itself could not be resolved — that is a load failure, not
        // "this process has no inputs", and the two must not look the same.
        <div
          className="space-y-3 rounded-md border border-warn-border bg-warn-bg p-4 text-sm text-ink-2"
          data-testid="configurator-module-unavailable"
        >
          <p>
            {tQuote("config.moduleUnavailable", {
              defaultValue:
                "Konfigurátor se teď nepodařilo načíst. Zkuste to prosím znovu.",
            })}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void processesQuery.refetch()}
            data-testid="configurator-module-retry"
          >
            {tQuote("config.moduleRetry", { defaultValue: "Zkusit znovu" })}
          </Button>
        </div>
      ) : (
        <DynamicPrimaryConfigurator
          catalog={catalogQuery.data}
          analysis={quote.analysis}
          lang={lang}
          itemSlug={quote.id}
          onAdded={navigateAfterAdd}
          editLine={editLine}
          inBatch={batchInfo !== null}
          processType={processName}
          schema={resolvedModule.configurator_schema}
          onBoundColorsChange={setBoundColors}
          publicInputDimensionKeys={quote.public_input_dimension_keys}
          quoteSlug={quote.id}
          autoRepairAvailable={quote.auto_repair_available === true}
          repairInUse={repairQuery.data?.uses_repaired_for_pricing === true}
          originalPreviewUrl={quote.preview_url}
          fileName={quote.file_name}
          isStaged={quote.is_staged === true}
          storedConfig={
            quote.operations?.find((op) => op.order_index === 0)
              ?.config_snapshot ?? null
          }
          reanalyzing={interactive.reanalyzing}
          analysisConfigKeys={
            quote.operations?.find((op) => op.order_index === 0)?.analysis_config_keys ?? null
          }
          onAnalysisInputsSaved={interactive.refresh}
        />
      )}
    </ConfiguratorPageShell>
  );
}
